/* global globalThis */
import React, { useState, useEffect, useRef } from "react";
import { Plus, Database, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../api-client/AxiosClient";
import CrateDb from "../../../../assets/crateDb.png";
import { toast } from "react-toastify";
import "./DataSource.css";
import Modal from "../../../common/Modal/Modal";
import { fieldConfig, getFieldConfigKey } from "./dataSourceFieldConfig";
import { showError } from "../../../common/Utils";
import { fetchAESKey, encryptText, isKeyLoaded } from "../../../../utils/encryption";
import {
  FaEdit,
  FaFileExcel,
  FaFileCode,
  FaNetworkWired,
  FaServer,
  FaCloud,
  FaSalesforce,
} from "react-icons/fa";
import { SiPostgresql } from "react-icons/si";
import sql from "../../../../assets/sql1.png";
import IBMDB2logo1 from "../../../../assets/IBMDB2logo1.png";


const DatabaseConnectionsTable = () => {
  const navigate = useNavigate();
  const { DataSource } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || "{}",
  );
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const mappingDBsRef = useRef([]);


  const tableHeadings = [
    "Name",
    "Description",
    "Data Source Type",
    "Created On",
    "Status",
    "Actions",
  ];

  const getDBTypeIcon = (dbType) => {
    const iconProps = "w-6 h-6 mr-2";

    console.log(dbType);
    switch (dbType?.toLowerCase()) {
      case "json":
        return <FaFileCode className={`${iconProps} text-yellow-600`} />;
      case "xml":
        return <FaFileCode className={`${iconProps} text-orange-600`} />;
      case "excel/csv":
      case "csv":
      case "excel":
        return <FaFileExcel className={`${iconProps} text-green-600`} />;
      case "api":
        return <FaNetworkWired className={`${iconProps} text-blue-600`} />;
      case "postgresql":
      case "postgres":
      case "postgressql":
        return <SiPostgresql className={`${iconProps} text-blue-700`} />;
      case "mysql":
      case "MsSql":
      case "mssql":
        return <img src={sql} alt="sql" className="w-8 h-8 mr-2" />;
      case "sql server":
      case "sqlserver":
        return <FaServer className={`${iconProps} text-red-600`} />;
      case "ibm db2":
      case "db2":
        return <img src={IBMDB2logo1} alt="CrateDb" className={iconProps} />;
      case "sap hana":
      case "hana":
        return <FaCloud className={`${iconProps} text-teal-600`} />;
      case "cratedb":
        return <img src={CrateDb} alt="CrateDb" className={iconProps} />;
      case "salesforce":
        return <FaSalesforce className={`${iconProps} text-[#007bff]`} />;
      default:
        return <FaServer className={`${iconProps} text-gray-600`} />;
    }
  };

  useEffect(() => {
    try {
      const storedConfig = localStorage.getItem("organization_config_id");
 

      if (storedConfig) {
        // Don't parse it - use the string value directly
   
        setSelectedConfiguration(storedConfig);
      }
    } catch (error) {
      console.error("Invalid configuration data:", error);
      setError("Failed to load configuration data");
      localStorage.removeItem("organization_config_id");
    }
  }, []);
  // 3. Add listener for localStorage changes
  // Replace the storage change useEffect (around line 95) with this:
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "organization_config_id") {
        try {
          // Use the string value directly, don't parseInt
          const newConfigId = e.newValue || null;
          setSelectedConfiguration(newConfigId);

          if (newConfigId) {
            fetchConnections(newConfigId);
          } else {
            setConnections([]);
          }
        } catch (error) {
          console.error("Invalid configuration data from storage:", error);
          localStorage.removeItem("organization_config_id");
          setError("Configuration data corrupted");
        }
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "organization_config_id") {
        try {
          // Use the string value directly, don't parseInt
          const newConfigId = e.detail.newValue || null;
          setSelectedConfiguration(newConfigId);

          if (newConfigId) {
            fetchConnections(newConfigId);
          } else {
            setConnections([]);
          }
        } catch (error) {
          console.error("Invalid configuration data from custom event:", error);
          setError("Configuration data corrupted");
        }
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener("customStorageChange", handleCustomStorageChange);

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange,
      );
    };
  }, []);

  // 4. Add useEffect to fetch connections when selectedCorporation changes (add after the above useEffect)
  // Replace the useEffect that triggers fetchConnections (around line 140) with this:
  useEffect(() => {
    if (selectedConfiguration) {
      fetchConnections(selectedConfiguration);
    }
  }, [selectedConfiguration]);

  // Fetch mapping DBs for edit form (mappingDbId)
  useEffect(() => {
    const fetchMappingDBs = async () => {
      try {
        const mappingResponse = await axiosClient.get("/api/MappingDB");
        if (mappingResponse?.data) {
          mappingDBsRef.current = mappingResponse.data;
        }
      } catch (err) {
        console.error("Error fetching mapping DBs:", err);
      }
    };
    fetchMappingDBs();
  }, []);


  // Fetch database connections based on corporation ID
  // Replace fetchConnections function (around line 155) with this:
  const fetchConnections = async (configurationId) => {
    if (!configurationId) {
      console.log("❌ No configuration ID provided");
      setConnections([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `/api/MappingDB/CorpMappingDBByUserCorp?OrganizationConfigId=${configurationId}`;
      console.log("🌐 API URL:", url);

      const response = await axiosClient.get(url);

      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        console.log("✅ Data fetched:", data.length, "connections");
        setConnections(data);
      } else {
        setConnections([]);
        toast.error("Failed to load database connections");
      }
    } catch (err) {
      console.error("❌ Error fetching connections:", err);
      setError(
        "Failed to fetch database connections: " +
          (err.message || "Unknown error"),
      );
      setConnections([]);
      toast.error(
        "Error loading database connections: " +
          (err.message || "Unknown error"),
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleAddDataSource = () => {
    navigate("/data-sources/add-data-source", {
      state: { selectedConfiguration },
    });
  };

  // Map connection from API to form field names (handles both camelCase and PascalCase)
  const connectionToFormData = (connection) => {
    const c = connection || {};
    const get = (...keys) => keys.reduce((acc, k) => (acc == null ? c[k] : acc), undefined);
    return {
      server: get("server", "Server") || "",
      database: get("database", "Database") || "",
      username: get("username", "Username") || "",
      password: "", // Never pre-fill password for security
      host: get("host", "Host") || "",
      port: get("port", "Port") ?? "",
      corporationDbName: get("organizationDbName", "corporationDbName", "OrganizationDbName", "name", "Name") || "",
      description: get("description", "Description") || "",
      isActive: get("isActive", "isactive", "IsActive") ?? true,
      mappingDbId: get("mappingdbId", "mappingDbId", "datasource_id", "MappingdbId") || "",
      consumerKey: get("consumerKey", "ConsumerKey") || "",
      consumerSecret: get("consumerSecret", "ConsumerSecret") || "",
      securityToken: get("securityToken", "SecurityToken") || "",
      CorporationDbName: get("organizationDbName", "OrganizationDbName", "CorporationDbName", "name", "Name") || "",
      url: get("url", "Url") || "",
      baseUrl: get("baseUrl", "BaseUrl", "apiUrl", "ApiUrl") || "",
      endpoint: get("endpoint", "Endpoint") || "",
      headers: get("headers", "Headers") || "",
      instanceName: get("instanceName", "InstanceName") || "",
      apiUrl: get("apiUrl", "ApiUrl") || "",
    };
  };

  // Map GetDataSourceConnection API response to form field names
  const apiResponseToFormData = (apiResponse, originalConnection) => {
    const conn = apiResponse?.connection || {};
    const parsed = apiResponse?.parsed || {};
    const orig = originalConnection || {};
    const getConn = (...keys) => keys.reduce((acc, k) => (acc != null && acc !== "" ? acc : conn[k]), undefined);
    const getParsed = (k) => parsed[k];

    return {
      server: getConn( "server", "Server")  || "",
      database: getConn("database", "Database") || getParsed("Database") || "",
      username: getConn("username", "Username") || getParsed("User ID") || "",
      password: "", // Never pre-fill password for security
      host: getConn("host", "Host") || getParsed("Data Source") || "",
      port: getConn("port", "Port") ?? "",
      corporationDbName: orig?.name || orig?.organizationDbName || orig?.corporationDbName || "",
      description: conn?.description || orig?.description || "",
      isActive: orig?.isActive ?? orig?.isactive ?? true,
      mappingDbId: orig?.mappingdbId || orig?.mappingDbId || orig?.datasource_id || "",
       clientId: conn?.clientId || getParsed("ClientId") || "",
  clientSecret: conn?.clientSecret || getParsed("ClientSecret") || "",
  securityToken: "",
  apiVersion: conn?.apiVersion || getParsed("ApiVersion") || "",
      CorporationDbName: orig?.name || orig?.organizationDbName || orig?.CorporationDbName || "",
      url: conn?.url || getParsed("ApiUrl") || "",
      baseURL: conn?.baseURL || getParsed("LoginUrl") || "",
      endpoint: conn?.apiUrl || conn?.endpoint || "",
      headers: conn?.headers || "",
      instanceName: conn?.instanceName || "",
      apiUrl: conn?.apiUrl || "",
    };
  };

  const handleEditDataSource = async (connection) => {
    const configKey = getFieldConfigKey(connection.dataSourceType);
    if (!configKey || !fieldConfig[configKey]) {
      toast.warning(
        `Edit form not available for "${connection.dataSourceType}" data source type.`
      );
      return;
    }

    setEditingConnection(connection);
    setEditFormData(connectionToFormData(connection)); // Fallback in case API fails
    setShowEditModal(true);
    setEditLoading(true);

    const storedConfig = localStorage.getItem("organization_config_id");
    if (!storedConfig) {
      toast.error("Organization configuration not found.");
      setEditLoading(false);
      return;
    }

    try {
      let url = `/api/MappingDB/GetDataSourceConnection?OrganizationConfigId=${storedConfig}`;
      const connId = connection.dataSourceId || connection.corpmappingdbid;
      if (connId) {
        url += `&CorpmappingDBId=${connId}`;
      }
      const response = await axiosClient.get(url);

      if (response?.data) {
        const formData = apiResponseToFormData(response.data, connection);
        setEditFormData(formData);
      }
    } catch (err) {
      console.error("Error fetching connection details:", err);
      toast.error(err?.response?.data?.message || "Failed to load connection details. Showing available data.");
      setEditFormData(connectionToFormData(connection));
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    let nextValue = value;
    if (type === "file") {
      nextValue = Array.from(files || []);
    } else if (type === "checkbox") {
      nextValue = checked;
    }
    setEditFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const isEditFormValid = () => {
    if (!editingConnection) return false;
    const configKey = getFieldConfigKey(editingConnection.dataSourceType);
    const fields = fieldConfig[configKey] || [];
    return fields.every((field) => {
      const value = editFormData[field.name];
      if (!field.required) return true;
     if (field.type === "file") {
        return Array.isArray(value) ? value.length > 0 : value instanceof File;
      }
      if (field.type === "checkbox") {
        return value === true;
      }
      return value !== undefined && value !== "" && value !== null;
    });
  };

  const buildEditPayload = () => {
    const c = editingConnection;
    const organizationConfigId = localStorage.getItem("organization_config_id");
    const storedCorp = JSON.parse(localStorage.getItem("selectedCorporation") || "{}");
    const organizationId = storedCorp?.value || null;

    const base = {
      organizationId: organizationId,
      organization_config_id: organizationConfigId || "",
      description: editFormData.description || "",
      isActive: c?.isActive ?? c?.isactive ?? true,
      organizationDbName: editFormData.corporationDbName || editFormData.CorporationDbName || "",
      datasource_id: c?.dataSourceId || c?.corpmappingdbid || editFormData.mappingDbId || c?.mappingdbId || "",
    };

    const configKey = getFieldConfigKey(c?.dataSourceType);
    if (["postgresql", "mysql", "sqlserver", "CrateDb", "db2", "ms_dynamics_ax", "sap_hana", "odoo"].includes(configKey)) {
      return {
        ...base,
        server: editFormData.server || "",
        database: editFormData.database || "",
        username: editFormData.username || "",
        password: editFormData.password ? encryptText(editFormData.password) : (c?.password || ""),
        host: editFormData.host || "",
        ...(configKey === "odoo" && { baseURL: editFormData.baseUrl || "" }),
        port: editFormData.port || "",
      };
    }
    if (configKey === "api") {
      return {
        ...base,
        organizationId: organizationId,
        database: "",
        server: "",
        username: "",
        password: "",
        host: editFormData.endpoint || editFormData.host || editFormData.apiUrl || "",
        port: "",
        headers: editFormData.headers || "",
        name: "API",
      };
    }
    if (configKey === "salesforce") {
      return {
        ...base,
        clientId: editFormData.clientId  || "",
        clientSecret: editFormData.clientSecret  || "",
        securityToken: editFormData.securityToken || "",
        username: editFormData.username || "",
        password: editFormData.password ? encryptText(editFormData.password) : (c?.password || ""),
        apiVersion: editFormData.apiVersion || "",
        baseURL: editFormData.baseURL || "",
        CorporationDbName: editFormData.CorporationDbName || "",




      };
    }
    return base;
  };

  const handleEditFileUpload = async () => {
    const organizationConfigId = localStorage.getItem("organization_config_id");
    const storedCorp = JSON.parse(localStorage.getItem("selectedCorporation") || "{}");
    const tenantId = storedCorp?.value || null;
    const dataToSend = new FormData();

    const fileValue = editFormData.file;
    let files = [];
    if (Array.isArray(fileValue)) {
      files = fileValue;
    } else if (fileValue) {
      files = [fileValue];
    }

    if (files.length === 0) {
      toast.error("Please select at least one file.");
      throw new Error("No files selected");
    }

    files.forEach((f) => dataToSend.append("files", f));
    dataToSend.append("storeFile", Boolean(editFormData.storeFile));
    dataToSend.append("organization_config_id", organizationConfigId || "");
    dataToSend.append("OrganizationDbName", editFormData.corporationDbName || "");
    dataToSend.append("datasource_id", editingConnection?.dataSourceId || editingConnection?.corpmappingdbid || editFormData.mappingDbId || "");
    dataToSend.append("Name", editFormData.corporationDbName || "");
    dataToSend.append("TenantId", tenantId || "");

    return axiosClient.post("/api/MappingDB/files", dataToSend, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const handleEditSubmit = async () => {
    if (!editingConnection) return;

    try {
      setEditLoading(true);
      if (!isKeyLoaded()) {
        await fetchAESKey();
      }

     const configKey = getFieldConfigKey(editingConnection.dataSourceType);
      let response;

      if (["xml", "excel_csv", "csv"].includes(configKey)) {
        response = await handleEditFileUpload();
      } else {
        const payload = buildEditPayload();
        response = await axiosClient.post(`/api/MappingDB/CorpMappingDB`, payload);
      }

      if (response?.status === 200 || response?.status === 204) {
        toast.success("Connection updated successfully!");
        globalThis.dispatchEvent(new CustomEvent("dataSourceAdded", {
  detail: { organizationConfigId: localStorage.getItem("organization_config_id") }
}));

        setShowEditModal(false);
        setEditingConnection(null);
        if (selectedConfiguration) {
          fetchConnections(selectedConfiguration);
        }
      } else {
        throw new Error("Update failed");
      }
    } catch (err) {
      showError(err);
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
            isActive ? "bg-green-400" : "bg-red-400"
          }`}
        ></span>
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Connections
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() =>
                selectedConfiguration && fetchConnections(selectedConfiguration)
              }
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show corporation selector first if no corporation is selected
  // Replace the check around line 360 with this:
  if (!selectedConfiguration) {
    return (
      <div className="flex items-center justify-center loader-height">
        <div className="p-6">
          <div className="text-center py-12">
            <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Configuration Selected
            </h3>
            <p className="text-gray-600">
              Please select a configuration from the settings to view data
              sources.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - show only Add Data Sources button
  if (connections.length === 0 && !loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Database Connections
          </h3>
          {DataSource?.canAdd && (
            <>
              <p className="text-gray-600 mb-6">
                Get started by adding your first database connection for{" "}
              </p>

              <button
                onClick={handleAddDataSource}
                className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Data Source
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Table view with data
  return (
    <>
      {connections.length > 0 ? (
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Registered Data Sources
              </h2>
              <p className="text-gray-600">
                Manage database connections for the selected configuration
              </p>
            </div>

            {DataSource?.canAdd && connections.length === 0 && (
              <div className="flex flex-col items-end space-y-4">
                <button
                  onClick={handleAddDataSource}
                  className="inline-flex items-center text-white px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Data Source
                </button>
              </div>
            )}
          </div>

          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {tableHeadings.map((heading) => {
                      const isCenterAligned = ["Status", "Actions"].includes(
                        heading,
                      );

                      if (!DataSource?.canAdd && heading === "Actions") {
                        return null;
                      }
                      return (
                        <th
                          key={heading}
                          className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            isCenterAligned ? "text-center" : "text-left"
                          }`}
                        >
                          {heading}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {loading ? (
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td colSpan="100%">
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-3 text-gray-600">
                            Loading database connections...
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody className="bg-white divide-y divide-gray-200">
                    {connections.map((connection) => (
                      <tr
                        key={connection.dataSourceId || connection.corpmappingdbid}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Database className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {connection.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm text-gray-900 max-w-xs truncate"
                            title={connection.description}
                          >
                            {connection.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-gray-900">
                          <div className="flex items-center justify-start">
                            {getDBTypeIcon(connection.dataSourceType)}
                            <span className="ml-2">
                              {connection.dataSourceType}
                            </span>
                          </div>
                        </td>
<td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {connection.created_at
                            ? new Date(connection.created_at).toLocaleString()
                            : "_"}
                        </td>

                        <td className="px-6 py-4 text-center align-middle">
                          {getStatusBadge(connection.isactive)}
                        </td>
                        {DataSource?.canAdd && (
                          <td className="px-6 py-4 text-center align-middle">
                            <button
                              onClick={() => handleEditDataSource(connection)}
                              className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-100 transition-colors"
                              title="Edit DataSource"
                            >
                              <FaEdit className="w-5 h-5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>

          <div className="my-4 text-sm text-gray-500">
            Showing {connections.length} database connection
            {connections.length === 1 ? "" : "s"} for the selected configuration
          </div>

          {/* Edit Data Source Modal */}
          <Modal
            show={showEditModal}
            title={`${editingConnection?.dataSourceType || "Data Source"} Configuration`}
            setShowModal={(show) => {
              setShowEditModal(show);
              if (!show) setEditingConnection(null);
            }}
            headerAction={
              <button
  type="button"
  className="
    inline-flex items-center gap-2
    px-4 py-2
    rounded-full
    text-sm font-medium
    text-blue-700
    bg-blue-100
    hover:bg-blue-200
    hover:shadow-sm
    transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
  "
  onClick={() => {
    setShowEditModal(false);
    handleAddDataSource();
  }}
  disabled={editLoading}
>
  Change Data Source
</button>
            }
          >
           {editingConnection && fieldConfig[getFieldConfigKey(editingConnection.dataSourceType)]?.map((field) => {
              if (field.name === "description") return null;
              const isRequired = field.required;
              const placeholder = isRequired ? `Enter ${field.label}` : `Enter ${field.label} (Optional)`;
              const isTextarea = field.type === "textarea";

              if (field.type === "checkbox") {
                if (!Array.isArray(editFormData.file) || editFormData.file.length === 0) return null;
                return (
                  <div key={field.name} style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "0 4px 12px 4px" }}>
                    <input
                      id={`edit-${field.name}`}
                      name={field.name}
                      type="checkbox"
                      checked={editFormData[field.name] || false}
                      onChange={handleEditInputChange}
                      style={{ marginTop: "3px", flexShrink: 0, width: "16px", height: "16px", cursor: "pointer", accentColor: "#2563eb" }}
                    />
                    <label htmlFor={`edit-${field.name}`} style={{ fontSize: "13px", color: "#374151", cursor: "pointer", lineHeight: "1.5" }}>
                     I consent to securely store this file for future use in data mapping and processing.
                    </label>
                  </div>
                );
              }

              return (
                <div key={field.name} className="modal-field">
                  <label htmlFor={`edit-${field.name}`}>
                    {field.label}
                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {isTextarea ? (
                    <textarea
                      id={`edit-${field.name}`}
                      name={field.name}
                      onChange={handleEditInputChange}
                      value={editFormData[field.name] || ""}
                      className={isRequired ? "required" : ""}
                      required={isRequired}
                      placeholder={placeholder}
                    />
                 ) : (
                    <input
                      id={`edit-${field.name}`}
                      name={field.name}
                      type={field.type}
                      value={field.type === "file" ? undefined : (editFormData[field.name] || "")}
                      onChange={handleEditInputChange}
                      className={isRequired ? "required" : ""}
                      required={isRequired}
                      placeholder={placeholder}
                      {...(field.multiple && { multiple: true })}
                    />
                  
                  )}
                </div>
              );
            })}

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`submit-btn ${isEditFormValid() ? "" : "disabled"}`}
                onClick={handleEditSubmit}
                disabled={!isEditFormValid() || editLoading}
              >
                {editLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </Modal>
        </div>
      ) : (
        <div className="flex items-center justify-center loader-height">
          <div className="p-6">
            <div className="text-center py-12">
              <div className="mb-6 flex justify-center">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DatabaseConnectionsTable;
